/**
 * Quick Judgment Game
 * Quickly judge if a word matches a phonics rule
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { PhonicsRule } from '../../../data/natural-phonics';
import { getWordEmoji } from '../../../data/phonics-images';
import { scoringRules, generateQuickJudgmentQuestions } from '../../../data/phonics-games';

interface QuickJudgmentGameProps {
  rules: PhonicsRule[];
  style: 'american' | 'british';
  onComplete: (score: number, stars: number) => void;
  onExit: () => void;
}

interface Question {
  word: string;
  pattern: string;
  ruleId: string;
  isCorrect: boolean;
  meaning: string;
}

export default function QuickJudgmentGame({
  rules,
  style,
  onComplete,
  onExit,
}: QuickJudgmentGameProps) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(5);
  const [isTimeUp, setIsTimeUp] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  // Generate questions
  useEffect(() => {
    const generated = generateQuickJudgmentQuestions(rules, 10);
    setQuestions(generated);
  }, [rules]);

  // Timer
  useEffect(() => {
    if (!currentQuestion || feedback || showResult) return;

    setTimeLeft(5);
    setIsTimeUp(false);

    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          setIsTimeUp(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [currentIndex, currentQuestion, feedback, showResult]);

  // Handle time up
  useEffect(() => {
    if (isTimeUp && !feedback && currentQuestion) {
      handleAnswer(false);
    }
  }, [isTimeUp, feedback, currentQuestion]);

  // Speak word
  const speakWord = useCallback(() => {
    if (!currentQuestion) return;

    const utterance = new SpeechSynthesisUtterance(currentQuestion.word);
    utterance.lang = style === 'american' ? 'en-US' : 'en-GB';
    utterance.rate = 0.8;
    speechSynthesis.speak(utterance);
  }, [currentQuestion, style]);

  // Speak on question change
  useEffect(() => {
    if (currentQuestion && !feedback) {
      setTimeout(() => speakWord(), 200);
    }
  }, [currentQuestion, feedback, speakWord]);

  // Handle answer
  const handleAnswer = (userSaysCorrect: boolean) => {
    if (feedback || !currentQuestion) return;

    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const isAnswerCorrect = userSaysCorrect === currentQuestion.isCorrect;

    if (isAnswerCorrect) {
      setFeedback('correct');
      setStreak(s => s + 1);
      setCorrectCount(c => c + 1);

      // Calculate points
      let points = scoringRules.quickJudgment.correct;
      if (timeLeft >= 3) {
        points += scoringRules.quickJudgment.speedBonus;
      }
      if (streak >= 2) {
        points += scoringRules.quickJudgment.streakBonus;
      }
      setScore(s => s + points);
    } else {
      setFeedback('wrong');
      setStreak(0);
      setScore(s => Math.max(0, s + scoringRules.quickJudgment.wrongPenalty));
    }

    // Move to next question
    setTimeout(() => {
      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex(i => i + 1);
        setFeedback(null);
        setIsTimeUp(false);
      } else {
        setShowResult(true);
      }
    }, 1200);
  };

  // Calculate stars
  const calculateStars = (accuracy: number): number => {
    if (accuracy >= 90) return 3;
    if (accuracy >= 70) return 2;
    if (accuracy >= 50) return 1;
    return 0;
  };

  // Handle game completion
  const handleComplete = () => {
    const accuracy = (correctCount / totalQuestions) * 100;
    const stars = calculateStars(accuracy);
    onComplete(score, stars);
  };

  // Show result screen
  if (showResult) {
    const accuracy = Math.round((correctCount / totalQuestions) * 100);
    const stars = calculateStars(accuracy);

    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center animate-bounce-in">
          <div className="text-6xl mb-4">⚡</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">快速判断完成！</h2>

          {/* Stars */}
          <div className="flex justify-center gap-2 my-6">
            {[1, 2, 3].map(i => (
              <span
                key={i}
                className={`text-4xl ${i <= stars ? 'animate-star-fly' : 'opacity-30'}`}
                style={{ animationDelay: `${i * 0.2}s` }}
              >
                ⭐
              </span>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 my-6">
            <div className="bg-purple-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-purple-600">{score}</p>
              <p className="text-sm text-gray-500">得分</p>
            </div>
            <div className="bg-pink-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-pink-600">{correctCount}/{totalQuestions}</p>
              <p className="text-sm text-gray-500">正确</p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-indigo-600">{accuracy}%</p>
              <p className="text-sm text-gray-500">正确率</p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onExit}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              返回
            </button>
            <button
              onClick={handleComplete}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold hover:shadow-lg transition-all"
            >
              确认
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-2xl text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 p-6">
      {/* Header */}
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onExit}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <span className="text-2xl">←</span>
            <span className="font-medium">退出</span>
          </button>

          <div className="flex items-center gap-4">
            <div className="bg-white px-4 py-2 rounded-xl shadow-soft">
              <span className="text-lg font-bold text-purple-600">{score}</span>
              <span className="text-sm text-gray-500 ml-1">分</span>
            </div>
            {streak >= 3 && (
              <div className="bg-orange-100 px-3 py-2 rounded-xl animate-pulse-glow">
                <span className="text-lg font-bold text-orange-600">🔥 {streak}</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>第 {currentIndex + 1} 题</span>
            <span>共 {totalQuestions} 题</span>
          </div>
          <div className="h-3 bg-white rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-purple-400 to-pink-400 transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
            />
          </div>
        </div>

        {/* Timer */}
        <div className="flex justify-center mb-6">
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold shadow-lg ${
              timeLeft > 3
                ? 'bg-green-100 text-green-600'
                : timeLeft > 1
                ? 'bg-yellow-100 text-yellow-600'
                : 'bg-red-100 text-red-600 animate-pulse'
            }`}
          >
            {timeLeft}
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-6 text-center">
          <p className="text-gray-500 mb-2">这个单词符合以下规则吗？</p>
          <p className="text-xl font-bold text-purple-600 mb-6">{currentQuestion.pattern}</p>

          {/* Word display */}
          <div className="bg-gray-50 rounded-2xl p-6 mb-6">
            <div className="flex items-center justify-center gap-4">
              <span className="text-5xl">{getWordEmoji(currentQuestion.word)}</span>
              <div>
                <p className="text-3xl font-bold text-gray-800">{currentQuestion.word}</p>
                <p className="text-gray-500">{currentQuestion.meaning}</p>
              </div>
            </div>
            <button
              onClick={speakWord}
              className="mt-4 text-2xl hover:scale-110 transition-transform"
            >
              🔊
            </button>
          </div>

          {/* Answer buttons */}
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleAnswer(true)}
              disabled={!!feedback}
              className={`py-6 rounded-2xl font-bold text-xl transition-all ${
                feedback
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-green-100 text-green-600 hover:bg-green-200 hover:scale-105 active:scale-95'
              }`}
            >
              ✅ 符合
            </button>
            <button
              onClick={() => handleAnswer(false)}
              disabled={!!feedback}
              className={`py-6 rounded-2xl font-bold text-xl transition-all ${
                feedback
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-red-100 text-red-600 hover:bg-red-200 hover:scale-105 active:scale-95'
              }`}
            >
              ❌ 不符合
            </button>
          </div>
        </div>

        {/* Feedback */}
        {feedback && (
          <div
            className={`bg-white rounded-2xl shadow-lg p-6 text-center animate-slide-up ${
              feedback === 'correct' ? 'border-2 border-green-400' : 'border-2 border-red-400'
            }`}
          >
            <div className="flex items-center justify-center gap-3">
              <span className="text-4xl">{feedback === 'correct' ? '✅ 正确！' : '❌ 错误'}</span>
            </div>
            <p className="mt-2 text-gray-600">
              {currentQuestion.word} {currentQuestion.isCorrect ? '符合' : '不符合'} {currentQuestion.pattern} 规则
            </p>
            {feedback === 'correct' && timeLeft >= 3 && (
              <p className="mt-2 text-green-500 font-bold">⚡ 速度奖励 +{scoringRules.quickJudgment.speedBonus}分!</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
